import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GigyaAPI, useGigyaStore } from '@ciam/gigya-api';
import { useMfaStore, useTenureExpiredCheck } from '@features/login';
import { useLoadingSpinnerStore, useModal } from '@ciam/ui';
import { useLoginInfoStore } from '@features/shared';
import { useTranslation } from '@ciam/i18n';
import { defaultInstance } from '@ciam/async-utils';

export const useMfaLogin = () => {
  const { openModal } = useModal();
  const { showLoading, hideLoading } = useLoadingSpinnerStore();
  const { t } = useTranslation(['login', 'schemas']);

  const [searchParams, setSearchParams] = useSearchParams();
  const jwtTokenResponse = searchParams.get('jwtTokenResponse') ?? '';
  const isBioOnly = searchParams.get('isBioOnly') ?? false;
  const channel = searchParams.get('channel') ?? '';

  const { setMfaSession, validateJwtTokenResponse } = useMfaStore();
  const { userInfo } = useLoginInfoStore();
  const isGigyaScriptLoaded = useGigyaStore((state) => state.isGigyaScriptLoaded);
  const { checkTenureExpired } = useTenureExpiredCheck();

  const removeJwtToken = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('jwtTokenResponse');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const checkMfaLoginSuccess = useCallback(async () => {
    const { email, uid, uidSignature, signatureTimestamp } = userInfo;

    const isSuccess = await validateJwtTokenResponse({
      jwtTokenResponse,
      isBioOnly: isBioOnly as boolean,
    });

    if (isSuccess === 'success') {
      try {
        await showLoading({
          title: t('success.mfa-success-title'),
          description: t('success.mfa-success-description'),
        });

        await setMfaSession({ email });

        await checkTenureExpired({ uid: uid ?? searchParams.get('UID') });
        await hideLoading();
        return await GigyaAPI.socialize.notifyLogin({
          dontHandleScreenSet: true,
          siteUID: uid,
          UIDSig: uidSignature,
          UIDTimestamp: signatureTimestamp,
        });
      } catch (error) {
        const err = error as Error;
        hideLoading();
        return openModal({
          type: 'error',
          description: [err.message],
        });
      }
    } else {
      // MFA 인증 실패 시
      await defaultInstance.post('/auditlog/create', {
        items: `MFA authentication was canceled/failed. Method:${isBioOnly === 'true' ? 'bioOnly' : 'all'}`,
        channel: channel,
        type: 'mfa-login',
        action: 'mfa-login-error',
        condition: uid,
      });

      return openModal({
        type: 'error',
        title: t('errors.mfa-error-title'),
        description: [t('errors.mfa-error-description')],
        onCancel: () => {
          removeJwtToken();
        },
      });
    }
  }, [
    channel,
    checkTenureExpired,
    isBioOnly,
    jwtTokenResponse,
    openModal,
    removeJwtToken,
    searchParams,
    setMfaSession,
    showLoading,
    t,
    userInfo,
    validateJwtTokenResponse,
  ]);

  // MFA 로그인 체크
  useEffect(() => {
    if (!isGigyaScriptLoaded) return;

    const isMfaLogin = jwtTokenResponse;
    if (isMfaLogin) checkMfaLoginSuccess();
  }, [isGigyaScriptLoaded, jwtTokenResponse, checkMfaLoginSuccess]);

  return { jwtTokenResponse, checkMfaLoginSuccess };
};
